import { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { SelectItemProps, usePrevious } from '@ui/src';
import { useAppDispatch } from '@store';
import { CustomerCard, OrderCardFormValues } from '../types';
import { OrganizationType } from '../../types';
import {
  clearOrderCardForm,
  getCardInfo,
  onSubmitCard,
} from '../../store/slices/cards';

import ROUTES from '../../../../routes';

const useCardOrder = ({
  type,
  customerId,
  cardId,
  cardInfo,
  countries,
}: {
  type: OrganizationType;
  cardId?: string;
  customerId?: string;
  cardInfo?: CustomerCard;
  countries?: SelectItemProps[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
    setValue,
  } = useForm<OrderCardFormValues>();

  const physical = watch('physical');
  const prevPhysical = usePrevious(physical);

  const onSubmit = handleSubmit((values) => {
    dispatch(onSubmitCard(values));
  });

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});

    setTimeout(() => {
      dispatch(clearOrderCardForm());

      navigate(
        generatePath(
          type === OrganizationType.CORPORATE
            ? ROUTES.CUSTOMERS_CORPORATE_CARDS
            : ROUTES.CUSTOMERS_INDIVIDUAL_CARDS,
          {
            customerId: customerId as string,
          },
        ),
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    if (customerId) {
      reset({
        userId: customerId,
      });
    }

    if (cardId) {
      dispatch(getCardInfo(cardId));
    }
  }, [dispatch, customerId, cardId]);

  useEffect(() => {
    if (cardInfo && countries && customerId) {
      const currentCountry = countries.find(
        (item) => item.value === cardInfo.countryId,
      );

      reset({
        userId: customerId,
        physical: !cardInfo.virtual,
        cardType: cardInfo.virtual ? 'virtual' : 'physical',
        paymentSystem: cardInfo.paymentSystem,
        countryId: cardInfo.countryId || '',
        city: cardInfo.city || '',
        postCode: cardInfo.postCode || '',
        street: cardInfo.street || '',
        apartment: cardInfo.apartment || '',
        countryName: currentCountry ? currentCountry.name : '',
      });
    }
  }, [cardInfo, customerId, countries]);

  useEffect(() => {
    if (!physical && prevPhysical) {
      setValue('countryId', '');
      setValue('city', '');
      setValue('street', '');
      setValue('apartment', '');
      setValue('postCode', '');
    }
  }, [physical, prevPhysical]);

  return {
    handleClose,
    control,
    errors,
    physical,
    setValue,
    panelIsOpen,
    isDirty,
    setDirtyPopupVisible,
    onSubmit,
    dirtyPopupVisible,
  };
};

export default useCardOrder;
